import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp } from 'antd';
import ErrorBoundary from './components/common/ErrorBoundary';
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import EmployeeLayout from './components/layout/EmployeeLayout';
import LoginForm from './components/auth/LoginForm';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import TimesheetEntry from './pages/TimesheetEntry';
import NewTimesheet from './pages/NewTimesheet';
import ApproveTimesheets from './pages/ApproveTimesheets';
import TeamOverview from './pages/TeamOverview';
import Projects from './pages/Projects';
import Profile from './pages/Profile';
import AdminDashboard from './components/admin/AdminDashboard';
import EmployeeManagement from './components/admin/EmployeeManagement';
import ProjectManagement from './components/admin/ProjectManagement';
import AdminProjectsGantt from './components/admin/AdminProjectsGantt';
import Reports from './pages/Reports';
import Documents from './pages/Documents';
import ProjectManagerDashboard from './components/dashboard/ProjectManagerDashboard';
import ProjectManagerLayout from './components/layout/ProjectManagerLayout';
import TaskManagementPage from './pages/TaskManagement';
import MyTasksPage from './pages/MyTasks';
import ResetPassword from './pages/ResetPassword';
import ApiTest from './components/test/ApiTest';
import MongoTest from './components/test/MongoTest';
import { useAuthStore } from './store/useAuthStore';
import './index.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (isAuthenticated) {
    // Redirect based on user role
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'project_manager') {
      return <Navigate to="/project-manager" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'admin') {
    return user?.role === 'project_manager' ? <Navigate to="/project-manager" replace /> : 
           <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Removed HR route component

const ProjectManagerRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'project_manager') {
    return user?.role === 'admin' ? <Navigate to="/admin" replace /> : 
           <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#2563eb',
            borderRadius: 6,
          },
        }}
      >
        <AntApp>
          <Router>
          <Routes>
            <Route 
              path="/login" 
              element={
                <Layout>
                  <PublicRoute>
                    <LoginForm />
                  </PublicRoute>
                </Layout>
              } 
            />
            
            <Route 
              path="/reset-password/:token" 
              element={
                <Layout>
                  <ResetPassword />
                </Layout>
              } 
            />
            
            {/* Employee Routes with Navigation */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <EmployeeLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="timesheet" element={<TimesheetEntry />} />
              <Route path="history" element={<History />} />
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="profile" element={<Profile />} />
              <Route index element={<Navigate to="/dashboard" replace />} />
            </Route>
            
            <Route 
              path="/test" 
              element={
                <Layout>
                  <ApiTest />
                </Layout>
              } 
            />
            <Route 
              path="/mongo-test" 
              element={
                <Layout>
                  <MongoTest />
                </Layout>
              } 
            />
            
            {/* Project Manager Routes */}
            <Route 
              path="/project-manager" 
              element={
                <ProjectManagerRoute>
                  <ProjectManagerLayout />
                </ProjectManagerRoute>
              }
            >
              <Route index element={<ProjectManagerDashboard />} />
              <Route path="projects" element={<Projects />} />
              <Route path="tasks" element={<TaskManagementPage />} />
              <Route path="my-tasks" element={<MyTasksPage />} />
              <Route path="timesheet" element={<TimesheetEntry />} />
              <Route path="approvals" element={<ApproveTimesheets />} />
              <Route path="team" element={<TeamOverview />} />
              <Route path="history" element={<History />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            {/* Removed HR Routes */}

            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="employees" element={<EmployeeManagement />} />
              <Route path="projects" element={<ProjectManagement />} />
              <Route path="timeline" element={<AdminProjectsGantt />} />
              <Route path="reports" element={<Reports />} />
              <Route path="documents" element={<Documents />} />
              <Route path="profile" element={<Profile />} />
            </Route>
            
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Router>
        </AntApp>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
